// src/pages/Dashboard/Dashboard.jsx

import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { collection, query, orderBy, onSnapshot, where, doc, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, Cell, PieChart, Pie, Legend } from 'recharts';
import * as XLSX from 'xlsx';
import TopUnshippedWidget from '../../components/dashboard/TopUnshippedWidget';
import toast from 'react-hot-toast';

const styles = {
    quickMenuCard: {
        background: 'white',
        padding: '16px',
        borderRadius: '16px',
        textDecoration: 'none',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '8px',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        border: '1px solid #f1f5f9',
        boxShadow: '0 2px 4px -1px rgba(0, 0, 0, 0.05)',
        cursor: 'pointer'
    },
    quickMenuIcon: {
        fontSize: '24px',
        background: '#f8fafc',
        width: '48px',
        height: '48px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '12px',
        transition: 'all 0.3s ease'
    },
    quickMenuLabel: {
        fontSize: '13px',
        fontWeight: '600',
        color: '#334155'
    },
    statsHeader: {
        marginBottom: '24px'
    },
    statsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '20px'
    },
    statItem: {
        background: 'rgba(255, 255, 255, 0.1)',
        padding: '20px',
        borderRadius: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '4px'
    },
    statLabel: {
        fontSize: '14px',
        opacity: 0.8,
        fontWeight: '500'
    },
    statValue: {
        fontSize: '24px',
        fontWeight: '700'
    },
    cardTitle: {
        fontSize: '18px',
        fontWeight: '700',
        color: '#0f172a',
        marginBottom: '20px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    },
    list: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px'
    },
    listItem: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '12px 16px',
        background: '#f8fafc',
        borderRadius: '12px',
        transition: 'all 0.2s ease',
        border: '1px solid transparent'
    },
    filterContainer: {
        display: 'flex',
        gap: '8px',
        background: '#f1f5f9',
        padding: '4px',
        borderRadius: '12px'
    },
    filterBtn: {
        padding: '8px 16px',
        borderRadius: '8px',
        border: 'none',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.2s ease'
    },
    activeFilter: {
        background: 'white',
        color: '#06C167',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
    },
    inactiveFilter: {
        background: 'transparent',
        color: '#64748b'
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: '#f8fafc'
    }
};

function Dashboard() {
    const { userData, currentUser, canViewPrices } = useAuth();
    const navigate = useNavigate();
    const [customers, setCustomers] = useState([]);
    const [orders, setOrders] = useState([]);
    const [payments, setPayments] = useState([]);
    const [companyData, setCompanyData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedOrders, setSelectedOrders] = useState([]);
    const [useDynamicRates, setUseDynamicRates] = useState(false);

    useEffect(() => {
        if (userData?.role === 'customer') {
            navigate('/portal');
        }
    }, [userData, navigate]);

    useEffect(() => {
        if (!userData?.companyId) {
            setLoading(false);
            return;
        }

        const companyId = userData.companyId;
        const unsubCompany = onSnapshot(doc(db, 'companies', companyId), (doc) => {
            if (doc.exists()) setCompanyData(doc.data());
        });

        const unsubCustomers = onSnapshot(query(collection(db, 'companies', companyId, 'customers')), (snapshot) => {
            setCustomers(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });

        const unsubOrders = onSnapshot(query(collection(db, 'companies', companyId, 'orders'), orderBy('orderDate', 'desc')), (snapshot) => {
            setOrders(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });

        const unsubPayments = onSnapshot(query(collection(db, 'companies', companyId, 'transactions'), where('type', '==', 'Tahsilat')), (snapshot) => {
            setPayments(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setLoading(false);
        });

        return () => {
            unsubCompany();
            unsubCustomers();
            unsubOrders();
            unsubPayments();
        };
    }, [userData]);

    const stats = useMemo(() => {
        const now = new Date();
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const endOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);

        const currentMonthOrders = orders.filter(o => {
            const d = o.orderDate?.seconds ? new Date(o.orderDate.seconds * 1000) : new Date(o.orderDate);
            return d >= startOfMonth;
        });

        const lastMonthOrders = orders.filter(o => {
            const d = o.orderDate?.seconds ? new Date(o.orderDate.seconds * 1000) : new Date(o.orderDate);
            return d >= startOfLastMonth && d <= endOfLastMonth;
        });

        const calculateTRY = (order) => {
            const amount = order.total || 0;
            const currency = order.currency || 'TRY';

            if (useDynamicRates) {
                if (currency === 'TRY') return amount;
                const rate = companyData?.currencySettings?.[currency];
                return rate ? amount * rate : 0;
            } else {
                return order.totalTRY || (currency === 'TRY' ? amount : 0);
            }
        };

        const currentRevenue = currentMonthOrders.reduce((sum, o) => sum + calculateTRY(o), 0);
        const lastRevenue = lastMonthOrders.reduce((sum, o) => sum + calculateTRY(o), 0);
        const revenueGrowth = lastRevenue === 0 ? 100 : ((currentRevenue - lastRevenue) / lastRevenue) * 100;

        const customerRevenue = {};
        payments.forEach(p => {
            if (p.customerId) customerRevenue[p.customerId] = (customerRevenue[p.customerId] || 0) + (p.amount || 0);
        });

        const topCustomers = Object.entries(customerRevenue)
            .map(([id, revenue]) => ({ id, revenue, name: customers.find(c => c.id === id)?.name || 'Bilinmeyen' }))
            .sort((a, b) => b.revenue - a.revenue)
            .slice(0, 5);

        // Chart Data
        const dailyData = {};
        currentMonthOrders.forEach(o => {
            const d = o.orderDate?.seconds ? new Date(o.orderDate.seconds * 1000) : new Date(o.orderDate);
            const key = d.toLocaleDateString('tr-TR', { day: '2-digit', month: 'short' });
            dailyData[key] = (dailyData[key] || 0) + calculateTRY(o);
        });

        const chartData = Object.entries(dailyData)
            .sort((a, b) => {
                const [d1] = a[0].split(' ');
                const [d2] = b[0].split(' ');
                return d1 - d2;
            })
            .map(([name, value]) => ({ name, value }));

        // Currency Distribution Data
        const currencyDataObj = {};
        currentMonthOrders.forEach(o => {
            const curr = o.currency || 'TRY';
            currencyDataObj[curr] = (currencyDataObj[curr] || 0) + 1;
        });
        const currencyData = Object.entries(currencyDataObj).map(([name, value]) => ({ name, value }));

        const COLORS = ['#06C167', '#10b981', '#f59e0b', '#ec4899', '#8b5cf6'];

        const handleExportSelectedOrders = async () => {
            if (selectedOrders.length === 0) {
                toast.error("Lütfen dışa aktarılacak siparişleri seçin.");
                return;
            }

            const companyId = userData.companyId;
            const exportData = [];

            try {
                for (const orderId of selectedOrders) {
                    const order = orders.find(o => o.id === orderId);
                    if (!order) continue;

                    // Fetch shipments for this order
                    const shipmentsSnapshot = await getDocs(collection(db, 'companies', companyId, 'orders', orderId, 'shipments'));
                    const orderShipments = shipmentsSnapshot.docs.map(doc => doc.data());

                    // Calculate shipped quantities
                    const shippedQuantities = {};
                    orderShipments.forEach(s => {
                        (s.items || []).forEach(item => {
                            shippedQuantities[item.productId] = (shippedQuantities[item.productId] || 0) + item.quantity;
                        });
                    });

                    // Add each item in the order to the export data
                    (order.lineItems || []).forEach(item => {
                        const shipped = shippedQuantities[item.productId] || 0;
                        const remaining = item.quantity - shipped;

                        exportData.push({
                            'Sipariş No': order.orderNumber || order.id.substring(0, 8).toUpperCase(),
                            'Müşteri': order.customerName,
                            'Tarih': order.orderDate?.seconds ? new Date(order.orderDate.seconds * 1000).toLocaleDateString('tr-TR') : '-',
                            'Ürün': item.productName || 'Bilinmeyen Ürün',
                            'Sipariş Miktarı': item.quantity,
                            'Gönderilen': shipped,
                            'Kalan (Gönderilmeyen)': remaining,
                            'Durum': order.status,
                            'Toplam Tutar': `${(item.total || 0).toLocaleString('tr-TR')} ${order.currency || '₺'}`
                        });
                    });
                }

                const ws = XLSX.utils.json_to_sheet(exportData);
                const wb = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb, ws, "Sipariş Detayları");
                XLSX.writeFile(wb, `Siparis_Detaylari_${new Date().toLocaleDateString('tr-TR').replace(/\./g, '-')}.xlsx`);

                setSelectedOrders([]); // Clear selection after export
            } catch (error) {
                console.error("Excel export hatası:", error);
                toast.error("Excel dosyası oluşturulurken bir hata oluştu.");
            }
        };

        const toggleOrderSelection = (orderId) => {
            setSelectedOrders(prev =>
                prev.includes(orderId) ? prev.filter(id => id !== orderId) : [...prev, orderId]
            );
        };

        return {
            currentRevenue,
            revenueGrowth,
            orderCount: currentMonthOrders.length,
            customerCount: customers.length,
            totalOrders: orders.length,
            topCustomers,
            chartData,
            currencyData,
            COLORS,
            recentOrders: orders.slice(0, 6),
            handleExportSelectedOrders,
            toggleOrderSelection,
            calculateTRY
        };
    }, [orders, payments, customers, selectedOrders, userData.companyId, companyData, useDynamicRates]);

    if (loading) return <div style={styles.loadingContainer}><span>Dashboard Hazırlanıyor...</span></div>;

    const quickMenus = [
        { label: 'Yeni Sipariş', icon: '📦', path: '/siparisler/yeni', color: '#06C167' },
        { label: 'Müşteriler', icon: '👥', path: '/musteriler', color: '#10b981' },
        { label: 'Yeni Tahsilat', icon: '💰', path: '/kasa', color: '#f59e0b' },
        { label: 'Yeni Fatura', icon: '🧾', path: '/faturalar/yeni', color: '#ec4899' },
        { label: 'Üretim Paneli', icon: '📊', path: '/uretim', color: '#8b5cf6' },
        { label: 'Lojistik Takip', icon: '🚚', path: '/lojistik', color: '#0ea5e9' },
    ];

    return (
        <div className="dashboard-container">
            <style>
                {`
                    @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700&display=swap');
                    
                    .dashboard-container {
                        font-family: 'Outfit', 'Inter', -apple-system, sans-serif;
                        background: #f8fafc;
                        padding: 32px;
                        min-height: 100vh;
                        color: #1e293b;
                    }

                    .dashboard-header {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        margin-bottom: 32px;
                        gap: 16px;
                    }

                    .welcome-section {
                        display: flex;
                        flex-direction: column;
                        gap: 4px;
                    }

                    .dashboard-title {
                        fontSize: 32px;
                        fontWeight: 700;
                        color: #0f172a;
                        margin: 0;
                        letter-spacing: -0.02em;
                    }

                    .dashboard-subtitle {
                        fontSize: 16px;
                        color: #64748b;
                        margin: 0;
                    }

                    .quick-menu-grid {
                        display: grid;
                        grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
                        gap: 16px;
                        margin-bottom: 40px;
                    }

                    .main-grid {
                        display: grid;
                        grid-template-columns: 2fr 1fr;
                        gap: 32px;
                        margin-bottom: 32px;
                    }

                    .bottom-grid {
                        display: grid;
                        grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
                        gap: 32px;
                    }

                    .glass-card {
                        background: rgba(255, 255, 255, 0.8);
                        backdrop-filter: blur(12px);
                        border-radius: 24px;
                        padding: 32px;
                        border: 1px solid rgba(255, 255, 255, 0.5);
                        box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.05), 0 10px 10px -5px rgba(0, 0, 0, 0.02);
                        position: relative;
                        overflow: hidden;
                    }

                    .gradient-card {
                        background: linear-gradient(135deg, #06C167 0%, #a855f7 100%);
                        border-radius: 24px;
                        padding: 32px;
                        color: white;
                        position: relative;
                        overflow: hidden;
                        box-shadow: 0 20px 25px -5px rgba(99, 102, 241, 0.3);
                    }

                    .quick-item:hover { transform: translateY(-5px); box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1); border-color: #06C167; }
                    .quick-item:hover .icon-bg { background: #eef2ff; color: #06C167; }
                    .list-item:hover { background: #f1f5f9; transform: translateX(5px); }

                    @media (max-width: 1024px) {
                        .main-grid {
                            grid-template-columns: 1fr;
                        }
                    }

                    @media (max-width: 768px) {
                        .dashboard-container {
                            padding: 16px;
                        }

                        .dashboard-header {
                            flex-direction: column;
                            align-items: flex-start;
                        }

                        .dashboard-title {
                            font-size: 24px;
                        }

                        .quick-menu-grid {
                            grid-template-columns: repeat(2, 1fr);
                            gap: 12px;
                        }

                        .bottom-grid {
                            grid-template-columns: 1fr;
                        }

                        .glass-card, .gradient-card {
                            padding: 24px;
                        }

                        .revenue-title {
                            font-size: 32px !important;
                        }
                    }

                    @media (max-width: 480px) {
                        .quick-menu-grid {
                            grid-template-columns: repeat(2, 1fr);
                        }
                        
                        .dashboard-title {
                            font-size: 20px;
                        }
                    }
                `}
            </style>

            <div className="dashboard-header">
                <div className="welcome-section">
                    <h1 className="dashboard-title">Merhaba, {currentUser?.email?.split('@')[0]} 👋</h1>
                    <p className="dashboard-subtitle">İşte işletmenizin bugünkü özeti.</p>
                </div>
                <div style={styles.filterContainer}>
                    <button
                        onClick={() => setUseDynamicRates(!useDynamicRates)}
                        style={{ ...styles.filterBtn, ...(useDynamicRates ? styles.activeFilter : styles.inactiveFilter), marginRight: '8px', border: '1px solid #e2e8f0' }}
                        title={useDynamicRates ? "Güncel kurlarla TL karşılığı (Settings'teki kurlar baz alınır)" : "Sipariş tarihindeki orijinal değerler"}
                    >
                        {useDynamicRates ? '💱 Güncel Kur (TR)' : '📅 İşlem Kuru (ORJ)'}
                    </button>
                    <button style={{ ...styles.filterBtn, ...styles.activeFilter }}>Bu Ay</button>
                </div>
            </div>

            {/* QUICK MENUS */}
            <div className="quick-menu-grid">
                {quickMenus.map((menu, idx) => (
                    <Link to={menu.path} key={idx} style={styles.quickMenuCard} className="quick-item">
                        <div style={{ ...styles.quickMenuIcon, color: menu.color }} className="icon-bg">
                            {menu.icon}
                        </div>
                        <span style={styles.quickMenuLabel}>{menu.label}</span>
                    </Link>
                ))}
            </div>

            <div className="main-grid">
                {/* REVENUE CARD */}
                <div className="gradient-card">
                    <div style={styles.statsHeader}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div>
                                <div style={{ fontSize: '16px', opacity: 0.9, fontWeight: '500' }}>Aylık Toplam Ciro</div>
                                <div className="revenue-title" style={{ fontSize: '44px', fontWeight: '800', margin: '8px 0' }}>
                                    {canViewPrices ? `${stats.currentRevenue.toLocaleString('tr-TR')} ₺` : '*** ₺'}
                                </div>
                            </div>
                            <div style={{ background: 'rgba(255,255,255,0.2)', padding: '8px 12px', borderRadius: '12px', fontSize: '14px', fontWeight: 'bold' }}>
                                {stats.revenueGrowth >= 0 ? '↑' : '↓'} {Math.abs(stats.revenueGrowth).toFixed(1)}%
                            </div>
                        </div>
                    </div>
                    <div style={styles.statsGrid}>
                        <div style={styles.statItem}>
                            <span style={styles.statLabel}>Sipariş Sayısı</span>
                            <span style={styles.statValue}>{stats.orderCount}</span>
                        </div>
                        <div style={styles.statItem}>
                            <span style={styles.statLabel}>Aktif Müşteri</span>
                            <span style={styles.statValue}>{stats.customerCount}</span>
                        </div>
                    </div>
                    <div style={{ marginTop: '24px' }}>
                        <Link to="/siparisler/faturalastirilmamis-sevkiyatlar" style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '14px',
                            background: 'rgba(255,255,255,0.2)',
                            borderRadius: '16px',
                            color: 'white',
                            textDecoration: 'none',
                            fontWeight: '600',
                            fontSize: '14px',
                            backdropFilter: 'blur(4px)',
                            border: '1px solid rgba(255,255,255,0.3)',
                            transition: 'all 0.2s'
                        }}>
                            🧾 Faturalaştırılmamış Sevkiyatlar
                        </Link>
                    </div>
                </div>

                {/* COMPANY INFO */}
                <div className="glass-card">
                    <h3 style={styles.cardTitle}>🏢 Firma Bilgileri</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <div>
                            <div style={{ fontSize: '12px', color: '#94a3b8', fontWeight: '600', textTransform: 'uppercase' }}>Firma Adı</div>
                            <div style={{ fontSize: '15px', fontWeight: '600', color: '#1e293b' }}>{companyData?.companyName || '-'}</div>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                            <div>
                                <div style={{ fontSize: '12px', color: '#94a3b8', fontWeight: '600', textTransform: 'uppercase' }}>Vergi No</div>
                                <div style={{ fontSize: '14px', fontWeight: '600' }}>{companyData?.taxNumber || '-'}</div>
                            </div>
                            <div>
                                <div style={{ fontSize: '12px', color: '#94a3b8', fontWeight: '600', textTransform: 'uppercase' }}>Telefon</div>
                                <div style={{ fontSize: '14px', fontWeight: '600' }}>{companyData?.phone || '-'}</div>
                            </div>
                        </div>
                        <div>
                            <div style={{ fontSize: '12px', color: '#94a3b8', fontWeight: '600', textTransform: 'uppercase' }}>Adres</div>
                            <div style={{ fontSize: '14px', color: '#475569', lineHeight: '1.5' }}>{companyData?.address || '-'}</div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="bottom-grid">
                {/* CHART CARD */}
                <div className="glass-card">
                    <h3 style={styles.cardTitle}>📈 Günlük Ciro Dağılımı</h3>
                    <div style={{ height: '260px', width: '100%', marginTop: '20px' }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={stats.chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#06C167" />
                                        <stop offset="100%" stopColor="#a855f7" />
                                    </linearGradient>
                                </defs>
                                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#94a3b8' }} />
                                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#94a3b8' }} />
                                <Tooltip
                                    cursor={{ fill: '#f1f5f9', radius: 8 }}
                                    contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)', padding: '12px' }}
                                    itemStyle={{ fontWeight: '600', color: '#06C167' }}
                                />
                                <Bar dataKey="value" fill="url(#barGradient)" radius={[6, 6, 0, 0]} barSize={24} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* CURRENCY PIE CHART */}
                <div className="glass-card">
                    <h3 style={styles.cardTitle}>💱 Para Birimi Dağılımı</h3>
                    <div style={{ height: '260px', width: '100%', marginTop: '20px' }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={stats.currencyData}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={60}
                                    outerRadius={80}
                                    paddingAngle={5}
                                    dataKey="value"
                                >
                                    {stats.currencyData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={stats.COLORS[index % stats.COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip
                                    contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }}
                                />
                                <Legend verticalAlign="bottom" height={36} />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* RECENT ORDERS */}
                <div className="glass-card">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '8px' }}>
                        <h3 style={{ ...styles.cardTitle, marginBottom: 0 }}>🕒 Son Siparişler</h3>
                        {selectedOrders.length > 0 && (
                            <button
                                onClick={stats.handleExportSelectedOrders}
                                style={{
                                    padding: '6px 12px',
                                    background: '#10b981',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontSize: '12px',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                }}
                            >
                                📥 Excel İndir ({selectedOrders.length})
                            </button>
                        )}
                    </div>
                    <div style={styles.list}>
                        {stats.recentOrders.map((order, idx) => (
                            <div
                                key={order.id}
                                style={{
                                    ...styles.listItem,
                                    background: selectedOrders.includes(order.id) ? '#f1f5f9' : '#f8fafc',
                                    borderColor: selectedOrders.includes(order.id) ? '#06C167' : 'transparent'
                                }}
                                className="list-item"
                                onClick={() => stats.toggleOrderSelection(order.id)}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <input
                                        type="checkbox"
                                        checked={selectedOrders.includes(order.id)}
                                        onChange={() => { }} // Handled by list item click
                                        style={{ accentColor: '#06C167', width: '16px', height: '16px' }}
                                    />
                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                        <span style={{ fontWeight: '600', fontSize: '14px' }}>{order.customerName}</span>
                                        <span style={{ fontSize: '12px', color: '#94a3b8' }}>
                                            {order.orderDate?.seconds
                                                ? new Date(order.orderDate.seconds * 1000).toLocaleDateString('tr-TR')
                                                : '-'
                                            }
                                        </span>
                                    </div>
                                </div>
                                <span style={{ fontWeight: '700', color: '#06C167', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                    {canViewPrices ? (
                                        useDynamicRates ? (
                                            <>
                                                <span>{stats.calculateTRY(order).toLocaleString('tr-TR', { maximumFractionDigits: 2 })} ₺</span>
                                                {order.currency && order.currency !== 'TRY' && (
                                                    <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '500' }}>
                                                        ({(order.total || 0).toLocaleString('tr-TR')} {order.currency === 'USD' ? '$' : (order.currency === 'EUR' ? '€' : (order.currency === 'GBP' ? '£' : order.currency))})
                                                    </span>
                                                )}
                                            </>
                                        ) : (
                                            `${(order.total || 0).toLocaleString('tr-TR')} ${order.currency === 'USD' ? '$' : (order.currency === 'EUR' ? '€' : (order.currency === 'GBP' ? '£' : '₺'))}`
                                        )
                                    ) : '***'}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* TOP CUSTOMERS */}
                <div className="glass-card">
                    <h3 style={styles.cardTitle}>🏆 En İyi Müşteriler</h3>
                    <div style={styles.list}>
                        {stats.topCustomers.map((c, idx) => (
                            <div key={c.id} style={styles.listItem} className="list-item">
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
                                    <div style={{ width: '32px', height: '32px', background: '#eef2ff', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', color: '#06C167', fontSize: '12px', flexShrink: 0 }}>
                                        {idx + 1}
                                    </div>
                                    <span style={{ fontWeight: '600', fontSize: '14px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</span>
                                </div>
                                <span style={{ fontWeight: '700', color: '#10b981', flexShrink: 0 }}>
                                    {canViewPrices ? `${c.revenue.toLocaleString('tr-TR')} ₺` : '***'}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* TOP UNSHIPPED */}
                <div className="glass-card">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h3 style={{ ...styles.cardTitle, marginBottom: 0 }}>📦 En Çok Bekleyenler</h3>
                        <Link to="/siparisler" style={{ fontSize: '13px', color: '#06C167', textDecoration: 'none', fontWeight: '600' }}>Tümü →</Link>
                    </div>
                    <TopUnshippedWidget orders={orders} />
                </div>
            </div>
        </div>
    );
}

export default Dashboard;
